import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import oldLogo from "@/assets/juen-logo.png";
import newLogoAsset from "@/assets/cuen-logo.png.asset.json";
import { setActiveLogoCache, type LogoVariant } from "@/components/juen-wordmark";

export const Route = createFileRoute("/harikaradmin/branding")({
  head: () => ({ meta: [{ title: "Logo — Admin" }] }),
  component: AdminBrandingPage,
});

function AdminBrandingPage() {
  const { user, isSuperadmin, loading } = useAuth();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<LogoVariant>("new");
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState<LogoVariant | null>(null);

  useEffect(() => {
    if (!loading && (!user || !isSuperadmin)) void navigate({ to: "/" });
  }, [loading, user, isSuperadmin, navigate]);

  useEffect(() => {
    if (!user || !isSuperadmin) return;
    void (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "active_logo")
        .maybeSingle();
      setVariant(data?.value === "old" ? "old" : "new");
      setPageLoading(false);
    })();
  }, [user, isSuperadmin]);

  const save = async (v: LogoVariant) => {
    if (!user) return;
    setSaving(v);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "active_logo", value: v, updated_by: user.id }, { onConflict: "key" });
    setSaving(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setVariant(v);
    setActiveLogoCache(v);
    toast.success(v === "old" ? "Eski logo aktif edildi." : "Yeni logo aktif edildi.");
  };

  if (loading || !user || !isSuperadmin || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-5xl px-4 py-10 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const options: Array<{ value: LogoVariant; label: string; src: string }> = [
    { value: "new", label: "Yeni logo", src: newLogoAsset.url },
    { value: "old", label: "Eski logo", src: oldLogo },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />

        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-lg font-semibold">Site logosu</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Sitede, biletlerde ve kuponlarda görünen logoyu buradan değiştirebilirsin.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {options.map((o) => {
              const active = variant === o.value;
              return (
                <div
                  key={o.value}
                  className={`rounded-xl border p-4 transition-colors ${
                    active ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <div className="flex h-24 items-center justify-center rounded-lg bg-background p-4">
                    <img src={o.src} alt={o.label} className="h-full w-full object-contain" />
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{o.label}</span>
                    {active ? (
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                        <Check className="h-4 w-4" /> Aktif
                      </span>
                    ) : (
                      <Button size="sm" onClick={() => void save(o.value)} disabled={saving !== null}>
                        {saving === o.value ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aktif et"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
